import { useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { insightsTracksResponseSchema } from '@internal/backstage-plugin-pulse-common';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';

/**
 * Loads insights track aggregates for a selected group scope.
 */
export const useInsightsTracks = (ownerScopeRefs: string[]) => {
  const { fetch } = useApi(fetchApiRef);
  const normalizedOwnerRefs = [...new Set(ownerScopeRefs)].sort((left, right) =>
    left.localeCompare(right),
  );
  const ownerScopeKey = normalizedOwnerRefs.join('|');

  const fetchInsights = useCallback(async () => {
    const query = new URLSearchParams();
    query.set('owners', normalizedOwnerRefs.join(','));

    const queryString = query.toString();
    const endpoint = 'plugin://pulse/insights/tracks';
    const requestUrl = queryString ? `${endpoint}?${queryString}` : endpoint;
    const response = await fetch(requestUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch insights tracks: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return insightsTracksResponseSchema.parse(data);
  }, [fetch, normalizedOwnerRefs]);

  return useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.insights(ownerScopeKey),
    fetcher: fetchInsights,
    ...PULSE_QUERY_CACHE_POLICIES.details,
    enabled: normalizedOwnerRefs.length > 0,
  });
};
